import { useEffect, useState } from 'react';
import Button from 'components/Button';
import { apiErrorMessage } from 'api/alert-slug';
import {
    fetchResearchSponsorshipAdmin,
    forceReleaseResearchSponsorship,
    setResearchSponsorHidden,
} from 'api/research-sponsorship';
import {
    RESEARCH_PRICES,
    RESEARCH_TEXT_MAX,
    priceLabel,
    researchAdminStatus,
    RESEARCH_TEXT_TOO_LONG,
    researchOfferLocked,
    researchReofferOpen,
    researchTextTooLong,
} from 'utils/research-sponsor';
import styles from 'styles/modules/newsAlert/Newsroom.module.scss';

import { news_alerts } from 'lang/en.json';

const copy = news_alerts.research_sponsor;

const ARM_MS = 5000;

// Once a buyer has reserved or paid, the offer is fixed; these act on the sale instead.
const LockedActions = ({ alertId, row, onRowChange, setNotice }) => {
    const [busy, setBusy] = useState(null);
    // The button clicked once; a second click within ARM_MS runs it.
    const [armed, setArmed] = useState(null);
    // Stripe would not expire the session: the editor may release it once checked.
    const [stillOpen, setStillOpen] = useState(false);

    useEffect(() => {
        if (!armed) return;
        const timer = setTimeout(() => setArmed(null), ARM_MS);
        return () => clearTimeout(timer);
    }, [armed]);

    const run = async (key, action, fallback) => {
        setBusy(key);
        setArmed(null);
        setNotice(null);
        let result;
        try {
            result = await action();
        } catch (err) {
            result = {
                type: 'error',
                text: apiErrorMessage(err?.response?.data, fallback),
            };
        }
        let fresh = null;
        try {
            fresh = await fetchResearchSponsorshipAdmin(alertId);
        } catch {
            result = {
                ...result,
                text: `${result.text} ${copy.admin_refresh_failed}`,
            };
        }
        setNotice(result);
        setBusy(null);
        // Last: a released offer is editable again, which unmounts these actions.
        if (fresh) onRowChange(fresh);
    };

    const reoffer = researchReofferOpen(row);

    const toggleHidden = () =>
        run(
            'hide',
            async () => {
                await setResearchSponsorHidden(alertId, !row.hidden);
                const shown = reoffer
                    ? copy.admin_reoffer_shown_ok
                    : copy.admin_shown_ok;
                const hidden = reoffer
                    ? copy.admin_reoffer_hidden_ok
                    : copy.admin_hidden_ok;
                return { type: 'ok', text: row.hidden ? shown : hidden };
            },
            copy.admin_sponsor_failed
        );

    const hideTitle = reoffer
        ? row.hidden
            ? copy.admin_show_reoffer
            : copy.admin_hide_reoffer
        : row.hidden
        ? copy.admin_show
        : copy.admin_hide;

    const release = (confirmedInStripe) =>
        run(
            confirmedInStripe ? 'confirmed' : 'release',
            async () => {
                const { status } = await forceReleaseResearchSponsorship(
                    alertId,
                    confirmedInStripe
                );
                setStillOpen(status === 'reserved');
                if (status === 'reserved') {
                    return { type: 'error', text: copy.admin_still_open };
                }
                return {
                    type: 'ok',
                    text:
                        status === 'sold'
                            ? copy.admin_now_sold
                            : copy.admin_released,
                };
            },
            copy.admin_release_failed
        );

    const confirmButton = (key, title, action) => (
        <Button
            title={armed === key ? copy.admin_confirm : title}
            theme={'danger'}
            loading={busy === key}
            disabled={Boolean(busy) && busy !== key}
            onClick={() => (armed === key ? action() : setArmed(key))}
        />
    );

    return (
        <>
            <div className={styles.editorActions}>
                {(row.status === 'sold' || reoffer) && (
                    <Button
                        title={hideTitle}
                        theme={'secondary'}
                        loading={busy === 'hide'}
                        onClick={toggleHidden}
                    />
                )}
                {row.status === 'reserved' &&
                    confirmButton('release', copy.admin_force_release, () =>
                        release(false)
                    )}
                {row.status === 'reserved' &&
                    stillOpen &&
                    confirmButton('confirmed', copy.admin_force_confirmed, () =>
                        release(true)
                    )}
            </div>
            {row.status === 'reserved' && (
                <p className={styles.hint}>{copy.admin_release_hint}</p>
            )}
        </>
    );
};

// Admins only. `row` is the saved offer from the admin endpoint, null before the
// first save; without `onRowChange` (alert create) there is no sale to act on.
// `legend` off where the page gives the section its own heading.
const ResearchSponsorFields = ({
    alertId,
    draft,
    row,
    onChange,
    onRowChange,
    legend = true,
}) => {
    // Here, not in LockedActions: a release makes the offer editable again and
    // unmounts them, and the notice has to outlive that.
    const [notice, setNotice] = useState(null);

    const notices = (
        <div aria-live="polite">
            {notice && (
                <p
                    className={
                        notice.type === 'error' ? styles.error : styles.ok
                    }
                >
                    {notice.text}
                </p>
            )}
        </div>
    );

    if (researchOfferLocked(row)) {
        return (
            <fieldset className={styles.faqOffer}>
                <legend className={styles.kicker}>{copy.admin_legend}</legend>
                <p className={styles.faqStatus}>
                    {researchAdminStatus(row)}
                    {row.hidden ? ` · ${copy.admin_hidden}` : ''}
                </p>
                <p className={styles.hint}>
                    {priceLabel(row.pricePence)} + VAT
                    {row.researchDetail ? ` · ${row.researchDetail}` : ''}
                </p>
                {onRowChange && (
                    <LockedActions
                        alertId={alertId}
                        row={row}
                        onRowChange={onRowChange}
                        setNotice={setNotice}
                    />
                )}
                {notices}
            </fieldset>
        );
    }

    return (
        <fieldset className={styles.faqOffer}>
            {legend && (
                <legend className={styles.kicker}>{copy.admin_legend}</legend>
            )}
            {researchReofferOpen(row) && (
                <>
                    <p className={styles.faqStatus}>
                        {researchAdminStatus(row)}
                        {row.hidden ? ` · ${copy.admin_hidden}` : ''}
                    </p>
                    <p className={styles.hint}>
                        {`${priceLabel(
                            row.pricePence
                        )} + VAT, fixed: the respondents were offered it at this price. `}
                        {copy.admin_reoffer_hint}
                    </p>
                    {onRowChange && (
                        <LockedActions
                            alertId={alertId}
                            row={row}
                            onRowChange={onRowChange}
                            setNotice={setNotice}
                        />
                    )}
                </>
            )}
            <label className={styles.checkboxLabel}>
                <input
                    type="checkbox"
                    checked={draft.enabled}
                    onChange={(e) => onChange('enabled', e.target.checked)}
                />
                {copy.admin_toggle}
            </label>
            <p className={styles.hint}>{copy.admin_hint}</p>
            {draft.enabled && !researchReofferOpen(row) && (
                <>
                    <label className={styles.label} htmlFor="research-detail">
                        {copy.admin_detail}{' '}
                        <span className={styles.count}>
                            ({draft.researchDetail.length}/{RESEARCH_TEXT_MAX})
                        </span>
                    </label>
                    <p className={styles.hint} id="research-detail-hint">
                        {copy.admin_detail_hint}
                    </p>
                    <textarea
                        id="research-detail"
                        aria-describedby="research-detail-hint"
                        className={styles.textarea}
                        rows={5}
                        maxLength={RESEARCH_TEXT_MAX}
                        required
                        value={draft.researchDetail}
                        onChange={(e) =>
                            onChange('researchDetail', e.target.value)
                        }
                    />
                    {researchTextTooLong(draft, row?.researchDetail) && (
                        <p className={styles.error} role="alert">
                            {RESEARCH_TEXT_TOO_LONG}
                        </p>
                    )}
                    <label className={styles.label} htmlFor="research-price">
                        {copy.admin_price}
                    </label>
                    <select
                        id="research-price"
                        className={styles.input}
                        value={draft.pricePence}
                        onChange={(e) =>
                            onChange('pricePence', Number(e.target.value))
                        }
                    >
                        {RESEARCH_PRICES.map((pence) => (
                            <option key={pence} value={pence}>
                                {`${priceLabel(pence)} + VAT`}
                            </option>
                        ))}
                    </select>
                </>
            )}
            {notices}
        </fieldset>
    );
};

export default ResearchSponsorFields;
